import React, { useCallback, useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';

import { MessageBubbleProps } from '@/types/chat';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import remarkNestHeaderBullets from '@/lib/remarkNestHeaderBullets';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { ThumbsUp, ThumbsDown, Flag, Copy } from 'lucide-react';
// import { createGapFromChatSecure } from '@/utils/secureApiCalls'; // Now imported dynamically
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/components/ui/hover-card';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
// Import intelligence components
import ConfidenceIndicator from '../intelligence/ConfidenceIndicator';
import FollowUpSuggestions from '../intelligence/FollowUpSuggestions';

const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  variant,
  showAvatar = false,
  showTimestamp = false,
  isStreaming = false,
  onExpandRequest
}) => {
  const isUser = variant === 'user';
  const { toast } = useToast();
  const [feedbackSent, setFeedbackSent] = useState<null | 'up' | 'down'>(null);
  // Detect citations either via structured array or bracket-style [n] or [n, m] references in content
  const bracketCitationIndexes = useMemo(() => {
    try {
      // Match both single numbers [n] and arrays [n, m, ...]
      const matches = Array.from(String(message?.content || '').matchAll(/\[([\d,\s]+)\]/g));
      const numbers: number[] = [];
      matches.forEach(match => {
        const citationStr = match[1];
        // Split by comma and parse each number
        const citationNumbers = citationStr.split(',')
          .map(s => s.trim())
          .map(s => parseInt(s, 10))
          .filter(n => !Number.isNaN(n));
        numbers.push(...citationNumbers);
      });
      return Array.from(new Set(numbers));
    } catch {
      return [] as number[];
    }
  }, [message?.content]);
  const hasStructuredCitations = Array.isArray((message as any).citations) && (message as any).citations.length > 0;
  const hasCitations = hasStructuredCitations || bracketCitationIndexes.length > 0;

  // Compute ordered unique bracket refs as the server does, so we can map [n] -> citations[n]
  const orderedUniqueBracketRefs = useMemo(() => {
    try {
      // Match both single numbers [n] and arrays [n, m, ...]
      const matches = Array.from(String(message?.content || '').matchAll(/\[([\d,\s]+)\]/g));
      const seen = new Set<number>();
      const ordered: number[] = [];
      matches.forEach(match => {
        const citationStr = match[1];
        // Split by comma and parse each number
        const citationNumbers = citationStr.split(',')
          .map(s => s.trim())
          .map(s => parseInt(s, 10))
          .filter(n => !Number.isNaN(n));
        for (const n of citationNumbers) {
          if (!seen.has(n)) {
            seen.add(n);
            ordered.push(n);
          }
        }
      });
      return ordered;
    } catch {
      return [] as number[];
    }
  }, [message?.content]);

  // Map each reference number to its citation object based on appearance order
  const refNumberToCitation = useMemo(() => {
    const map = new Map<number, any>();
    if (Array.isArray((message as any).citations) && (message as any).citations.length > 0) {
      orderedUniqueBracketRefs.forEach((refNum, idx) => {
        const citation = (message as any).citations[idx];
        if (citation) map.set(refNum, citation);
      });
    }
    return map;
  }, [(message as any).citations, orderedUniqueBracketRefs]);

  // Map original reference numbers -> display numbers (1..N by first mention)
  const displayNumberMap = useMemo(() => {
    const map = new Map<number, number>();
    orderedUniqueBracketRefs.forEach((refNum, idx) => map.set(refNum, idx + 1));
    return map;
  }, [orderedUniqueBracketRefs]);

  // Tooltip content renderer used by both highlight variants
  const CitationTooltip: React.FC<{ refNum: number }> = ({ refNum }) => {
    const citation = refNumberToCitation.get(refNum);
    const content = (
      <div>
        <div className="text-xs font-semibold mb-1">{citation?.title || 'Referenced context'}</div>
        {citation?.summary ? (
          <p className="text-xs text-muted-foreground leading-relaxed">{citation.summary}</p>
        ) : citation?.snippet ? (
          <p className="text-xs text-muted-foreground leading-relaxed">{citation.snippet}</p>
        ) : (
          <p className="text-xs text-muted-foreground leading-relaxed">Source details not available for this reference.</p>
        )}
      </div>
    );

    return (
      <>
        <span className="hidden md:inline">
          <HoverCard>
            <HoverCardTrigger asChild>
              <span className="inline-flex" aria-label={`View citation ${refNum}`} />
            </HoverCardTrigger>
            <HoverCardContent side="top" align="center" className="w-80">
              {content}
            </HoverCardContent>
          </HoverCard>
        </span>
        <span className="inline md:hidden">
          <Popover>
            <PopoverTrigger asChild>
              <span className="inline-flex" aria-label={`View citation ${refNum}`} />
            </PopoverTrigger>
            <PopoverContent side="top" align="center" className="w-80">
              {content}
            </PopoverContent>
          </Popover>
        </span>
      </>
    );
  };

  // Replace [n] or [n, m] markers with compact inline number pills and tooltips (only if citations exist)
  const transformTextWithPills = useCallback((input: string): React.ReactNode[] => {
    if (!hasStructuredCitations) return [input];
    // Split on both single citations [n] and array citations [n, m, ...]
    const parts = input.split(/(\[[\d,\s]+\])/g);
    const output: React.ReactNode[] = [];
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      // Match both single [n] and array [n, m, ...] formats
      const match = part.match(/^\[([\d,\s]+)\]$/);
      if (match) {
        const citationStr = match[1];
        const citationNumbers = citationStr.split(',')
          .map(s => s.trim())
          .map(s => parseInt(s, 10))
          .filter(n => !Number.isNaN(n));

        if (citationNumbers.length === 1) {
          // Single citation [n]
          const refNum = citationNumbers[0];
          const displayNum = displayNumberMap.get(refNum) || refNum;
          const pill = (
            <span className="inline-flex items-center justify-center rounded-full bg-primary text-white font-semibold px-1.5 py-[1px] text-[0.65em] leading-none ml-1 mr-1 align-super">
              {displayNum}
            </span>
          );
          output.push(
            <span key={`pill-${i}`} className="inline-flex align-super">
              <span className="hidden md:inline">
                <HoverCard>
                  <HoverCardTrigger asChild>
                    {pill}
                  </HoverCardTrigger>
                  <HoverCardContent side="top" align="center" className="w-80">
                    <div>
                      <div className="text-xs font-semibold mb-1">{refNumberToCitation.get(refNum)?.title || 'Referenced context'}</div>
                      {refNumberToCitation.get(refNum)?.summary ? (
                        <p className="text-xs text-muted-foreground leading-relaxed">{refNumberToCitation.get(refNum)?.summary}</p>
                      ) : refNumberToCitation.get(refNum)?.snippet ? (
                        <p className="text-xs text-muted-foreground leading-relaxed">{refNumberToCitation.get(refNum)?.snippet}</p>
                      ) : (
                        <p className="text-xs text-muted-foreground leading-relaxed">Source details not available for this reference.</p>
                      )}
                    </div>
                  </HoverCardContent>
                </HoverCard>
              </span>
              <span className="inline md:hidden">
                <Popover>
                  <PopoverTrigger asChild>
                    {pill}
                  </PopoverTrigger>
                  <PopoverContent side="top" align="center" className="w-80">
                    <div>
                      <div className="text-xs font-semibold mb-1">{refNumberToCitation.get(refNum)?.title || 'Referenced context'}</div>
                      {refNumberToCitation.get(refNum)?.summary ? (
                        <p className="text-xs text-muted-foreground leading-relaxed">{refNumberToCitation.get(refNum)?.summary}</p>
                      ) : refNumberToCitation.get(refNum)?.snippet ? (
                        <p className="text-xs text-muted-foreground leading-relaxed">{refNumberToCitation.get(refNum)?.snippet}</p>
                      ) : (
                        <p className="text-xs text-muted-foreground leading-relaxed">Source details not available for this reference.</p>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </span>
            </span>
          );
        } else if (citationNumbers.length > 1) {
          // Array citation [n, m, ...] - render multiple pills
          const pills = citationNumbers.map((refNum, idx) => {
            const displayNum = displayNumberMap.get(refNum) || refNum;
            const pill = (
              <span className="inline-flex items-center justify-center rounded-full bg-primary text-white font-semibold px-1.5 py-[1px] text-[0.65em] leading-none align-super">
                {displayNum}
              </span>
            );
            return (
              <span key={`pill-${i}-${idx}`} className="inline-flex align-super">
                {idx > 0 && <span className="text-xs text-muted-foreground mx-0.5">,</span>}
                <span className="hidden md:inline">
                  <HoverCard>
                    <HoverCardTrigger asChild>
                      {pill}
                    </HoverCardTrigger>
                    <HoverCardContent side="top" align="center" className="w-80">
                      <div>
                        <div className="text-xs font-semibold mb-1">{refNumberToCitation.get(refNum)?.title || 'Referenced context'}</div>
                        {refNumberToCitation.get(refNum)?.summary ? (
                          <p className="text-xs text-muted-foreground leading-relaxed">{refNumberToCitation.get(refNum)?.summary}</p>
                        ) : refNumberToCitation.get(refNum)?.snippet ? (
                          <p className="text-xs text-muted-foreground leading-relaxed">{refNumberToCitation.get(refNum)?.snippet}</p>
                        ) : (
                          <p className="text-xs text-muted-foreground leading-relaxed">Source details not available for this reference.</p>
                        )}
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                </span>
                <span className="inline md:hidden">
                  <Popover>
                    <PopoverTrigger asChild>
                      {pill}
                    </PopoverTrigger>
                    <PopoverContent side="top" align="center" className="w-80">
                      <div>
                        <div className="text-xs font-semibold mb-1">{refNumberToCitation.get(refNum)?.title || 'Referenced context'}</div>
                        {refNumberToCitation.get(refNum)?.summary ? (
                          <p className="text-xs text-muted-foreground leading-relaxed">{refNumberToCitation.get(refNum)?.summary}</p>
                        ) : refNumberToCitation.get(refNum)?.snippet ? (
                          <p className="text-xs text-muted-foreground leading-relaxed">{refNumberToCitation.get(refNum)?.snippet}</p>
                        ) : (
                          <p className="text-xs text-muted-foreground leading-relaxed">Source details not available for this reference.</p>
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                </span>
              </span>
            );
          });
          output.push(<span key={`pills-${i}`} className="inline-flex align-super">{pills}</span>);
        }
      } else if (part) {
        output.push(part);
      }
    }
    return output;
  }, [hasStructuredCitations, displayNumberMap, refNumberToCitation]);

  const renderWithPills = useCallback((children: React.ReactNode) => {
    return React.Children.map(children as any, (child) => {
      if (typeof child === 'string') {
        return <>{transformTextWithPills(child)}</>;
      }
      return child;
    });
  }, [transformTextWithPills]);

  const sendRating = useCallback(async (rating: number) => {
    try {
      await supabase.functions.invoke('track-analytics', {
        body: {
          event_type: 'answer_rated',
          posit_id: (message as any).posit_id,
          message_id: message.id,
          rating,
        }
      });
      // Emit explicit thumbs events for KPIs
      await supabase.functions.invoke('track-analytics', {
        body: {
          event_type: rating >= 3 ? 'thumbs_up' : 'thumbs_down',
          posit_id: (message as any).posit_id,
          message_id: message.id,
        }
      });
      setFeedbackSent(rating >= 3 ? 'up' : 'down');
      toast({ title: 'Thanks for your feedback!' });

      // If low rating, create a real gap task for the creator
      if (rating <= 2) {
        try {
          const { createGapFromChatSecure } = await import('@/utils/secureApiCalls');
          await createGapFromChatSecure({
            posit_id: (message as any).posit_id,
            user_id: (message as any).created_by || null,
            message_id: message.id,
            question: (message as any).user_question || 'Question not captured',
            context: message.content,
            reason: 'thumbs_down'
          });
        } catch (error) {
          console.warn('Failed to create gap from chat:', error);
        }
      }
    } catch (e: any) {
      toast({ title: "Couldn't send feedback", description: e?.message || 'Please try again.', variant: 'destructive' });
    }
  }, [message, toast, hasCitations]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(String(message?.content || ''));
      toast({ title: 'Copied to clipboard' });
    } catch (e: any) {
      toast({ title: 'Copy failed', description: e?.message || 'Please try again.', variant: 'destructive' });
    }
  }, [message?.content, toast]);
  
  // Follow-up selection handler
  const handleFollowUpClick = useCallback((question: string) => {
    if (onExpandRequest) {
      onExpandRequest(question);
    }
  }, [onExpandRequest]);
  
  const hasContent = String(message?.content || '').trim().length > 0;
  
  // Check if the message has intelligence data
  const hasConfidenceIndicator = !isUser && message.smartMetadata?.confidence !== undefined;
  const hasFollowUps = !isUser && message.improvements?.followUp && message.improvements.followUp.length > 0;
  
  return (
    <div
      className={cn(
        "group px-6 py-8 md:px-8 md:py-6 transition-all duration-200",
        // Subtle background for better visual separation
        "bg-gradient-to-r from-transparent via-muted/5 to-transparent",
        // Enhanced hover effect for better interactivity
        "hover:from-muted/10 hover:to-muted/10"
      )}
      data-message-id={message.id}
    >
      {/* Message Content */}
      <div className="max-w-4xl mx-auto">
        {isUser ? (
          <div className="flex justify-end">
            <div className="max-w-[85%] md:max-w-[80%] bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 text-foreground px-5 py-4 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
              <p className="text-sm md:text-base font-normal whitespace-pre-wrap leading-relaxed">{message.content}</p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="prose dark:prose-invert max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 bg-card/50 rounded-xl p-4 md:p-6 border border-border/30 shadow-sm">
              {hasContent ? (
                <>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm, remarkBreaks, remarkNestHeaderBullets]}
                    components={{
                      p: ({ children }) => (
                        <p className="mb-4 last:mb-0 text-sm font-normal text-foreground leading-relaxed">
                          {renderWithPills(children)}
                        </p>
                      ),
                      ul: ({ children }) => <ul className="list-disc pl-6 space-y-2 mb-4 last:mb-0">{children}</ul>,
                      ol: ({ children }) => <ol className="list-decimal pl-6 space-y-2 mb-4 last:mb-0">{children}</ol>,
                      li: ({ children }) => (
                        <li className="text-sm font-normal text-foreground leading-relaxed">
                          {renderWithPills(children)}
                        </li>
                      ),
                      h1: ({ children }) => (
                        <div className="flex items-center gap-2 mt-6 mb-4 first:mt-0">
                          <h1 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight">{children}</h1>
                        </div>
                      ),
                      h2: ({ children }) => {
                        // Extract emoji from title for special styling
                        const text = String(children);
                        const emojiMatch = text.match(/^([^\w\s]*)/);
                        const emoji = emojiMatch ? emojiMatch[1] : '';
                        const title = emoji ? text.slice(emoji.length).trim() : text;

                        return (
                          <div className="flex items-center gap-3 mt-6 mb-3 first:mt-0 p-3 bg-muted/30 rounded-lg border-l-4 border-primary/60">
                            {emoji && <span className="text-2xl">{emoji}</span>}
                            <h2 className="text-xl md:text-2xl font-bold text-foreground tracking-tight">{title}</h2>
                          </div>
                        );
                      },
                      h3: ({ children }) => {
                        // Extract emoji from subtitle for special styling
                        const text = String(children);
                        const emojiMatch = text.match(/^([^\w\s]*)/);
                        const emoji = emojiMatch ? emojiMatch[1] : '';
                        const title = emoji ? text.slice(emoji.length).trim() : text;

                        return (
                          <div className="flex items-center gap-2 mt-5 mb-2">
                            {emoji && <span className="text-lg">{emoji}</span>}
                            <h3 className="text-lg font-semibold text-foreground">{title}</h3>
                          </div>
                        );
                      },
                      blockquote: ({ children }) => (
                        <blockquote className="my-4 border-l-4 border-primary/40 pl-4 py-2 bg-muted/20 rounded-r-md italic text-muted-foreground">
                          {children}
                        </blockquote>
                      ),
                      hr: () => <hr className="my-6 border-border/40" />,
                      table: ({ children }) => (
                        <div className="my-4 overflow-x-auto rounded-lg border border-border/50 shadow-sm">
                          <table className="w-full text-sm">{children}</table>
                        </div>
                      ),
                      thead: ({ children }) => <thead className="bg-muted/60 border-b border-border/50">{children}</thead>,
                      tbody: ({ children }) => <tbody className="divide-y divide-border/30">{children}</tbody>,
                      tr: ({ children }) => <tr className="hover:bg-muted/20 transition-colors">{children}</tr>,
                      th: ({ children }) => (
                        <th className="text-left px-4 py-3 font-semibold text-foreground/90 bg-muted/40">{children}</th>
                      ),
                      td: ({ children }) => <td className="px-4 py-3 align-top text-foreground/80">{children}</td>,
                      a: ({ href, children }) => (
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:text-primary/80 underline underline-offset-2 transition-colors font-medium"
                        >
                          {children}
                        </a>
                      ),
                      code: ({ children }) => (
                        <code className="bg-muted/80 px-2 py-1 rounded-md text-sm font-mono border border-border/40 text-foreground/90">
                          {children}
                        </code>
                      ),
                      pre: ({ children }) => (
                        <pre className="bg-muted/60 p-4 rounded-lg overflow-x-auto text-sm border border-border/40 shadow-inner">
                          {children}
                        </pre>
                      ),
                      strong: ({ children }) => (
                        <strong className="font-semibold text-foreground">
                          {children}
                        </strong>
                      ),
                      em: ({ children }) => (
                        <em className="italic text-foreground/90">
                          {children}
                        </em>
                      )
                    }}
                  >
                    {message.content}
                  </ReactMarkdown>
                  {isStreaming && (
                    <span className="inline-block w-3 h-5 bg-foreground animate-pulse ml-1" />
                  )}
                </>
              ) : (
                // Tiny typing bubble inside the existing container until first tokens arrive
                <div className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-muted/60 border border-border/50">
                  <span className="w-1.5 h-1.5 bg-muted-foreground/70 rounded-full animate-bounce" />
                  <span className="w-1.5 h-1.5 bg-muted-foreground/70 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                  <span className="w-1.5 h-1.5 bg-muted-foreground/70 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                </div>
              )}
            </div>

            {/* Removed bottom citation chips per new UX: citations are shown only inline via superscripts */}
            {/* Removed fallback chips as well; inline superscripts remain as the sole entry point */}
          </div>
        )}
        
        {/* Timestamp */}
        {showTimestamp && message.created_at && (
          <div className={cn(
            "mt-2 text-xs text-muted-foreground",
            isUser ? "text-right" : "text-left"
          )}>
            {format(new Date(message.created_at), 'h:mm a')}
          </div>
        )}
        
        {/* Assistant utilities: feedback + copy with enhanced styling */}
        {!isUser && hasContent && (
          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {/* Intelligence: Confidence Indicator */}
              {hasConfidenceIndicator && message.smartMetadata && (
                <ConfidenceIndicator score={message.smartMetadata.confidence} />
              )}
            </div>

            <div className="flex items-center gap-1 bg-muted/30 rounded-lg p-1 border border-border/20">
              {!feedbackSent ? (
                <>
                  <button
                    aria-label="Mark helpful"
                    onClick={() => sendRating(5)}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs text-muted-foreground hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-950/20 rounded transition-all"
                  >
                    <ThumbsUp className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Helpful</span>
                  </button>
                  <button
                    aria-label="Mark not helpful"
                    onClick={() => sendRating(1)}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 rounded transition-all"
                  >
                    <ThumbsDown className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Not helpful</span>
                  </button>
                </>
              ) : (
                <div className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded">
                  {feedbackSent === 'up' ? (
                    <>
                      <ThumbsUp className="h-3.5 w-3.5 text-green-600" />
                      <span className="hidden sm:inline text-green-600">Helpful</span>
                    </>
                  ) : (
                    <>
                      <ThumbsDown className="h-3.5 w-3.5 text-red-600" />
                      <span className="hidden sm:inline text-red-600">Not helpful</span>
                    </>
                  )}
                </div>
              )}

              {/* Copy message content */}
              <button
                aria-label="Copy message"
                onClick={handleCopy}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-all"
              >
                <Copy className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Copy</span>
              </button>
            </div>
          </div>
        )}

        {/* File Attachments with enhanced styling */}
        {message.attachments && message.attachments.length > 0 && (
          <div className={cn(
            "mt-4 flex flex-wrap gap-3",
            isUser ? "justify-end" : "justify-start"
          )}>
            {message.attachments.map((file, index) => (
              <div
                key={file.id || index}
                className="max-w-[240px] rounded-xl p-4 cursor-pointer hover:bg-muted/60 hover:shadow-md transition-all duration-200 border border-border/40 bg-gradient-to-br from-muted/20 to-muted/10 hover:border-primary/30"
              >
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                    <span className="text-sm">📎</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate text-foreground/90">{file.name}</div>
                    <div className="text-xs text-muted-foreground/80 mt-0.5">
                      {file.source === 'google_drive' ? '📁 Google Drive' : '💾 Local Upload'}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Intelligence: Follow-up Suggestions */}
        {hasFollowUps && message.improvements?.followUp && !isStreaming && (
          <FollowUpSuggestions 
            questions={message.improvements.followUp} 
            onSelect={handleFollowUpClick} 
          />
        )}
      </div>
    </div>
  );
};

export default MessageBubble;